import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit3, Upload } from 'lucide-react';
import { AppLayout } from '../../components/layout/AppLayout';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import {
  productsApi,
  VendorProduct,
  ProductStatus,
} from '../../api/products';
import { formatNgn, formatDate } from '../../utils/format';
import { ProductImportModal } from './ProductImportModal';

const STATUS_VARIANT: Record<
  ProductStatus,
  'success' | 'warning' | 'neutral'
> = {
  active: 'success',
  draft: 'warning',
  archived: 'neutral',
};

export const Products: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [products, setProducts] = useState<VendorProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ProductStatus | 'all'>(
    'all',
  );
  const [search, setSearch] = useState('');

  const [importOpen, setImportOpen] = useState(false);
  const [initialImportJobId, setInitialImportJobId] = useState<string | null>(
    null,
  );

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const data = await productsApi.list({
        status: statusFilter === 'all' ? undefined : statusFilter,
        q: search.trim() || undefined,
      });
      setProducts(data.items);
    } catch (err) {
      toast.error(err, 'Failed to load products.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, toast]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jobId = params.get('shopifyImportJobId');
    const error = params.get('shopifyImportError');
    if (jobId) {
      setInitialImportJobId(jobId);
      setImportOpen(true);
      toast.success('Shopify store connected. Review the import preview.');
    }
    if (error) {
      toast.error(error, 'Could not connect Shopify.');
    }
    if (jobId || error) {
      params.delete('shopifyImportJobId');
      params.delete('shopifyStore');
      params.delete('shopifyImportError');
      const nextSearch = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
      );
    }
  }, [toast]);

  async function handleArchive(p: VendorProduct) {
    if (
      !window.confirm(
        `Archive "${p.name}"? It will be hidden from the marketplace but past orders remain unchanged.`,
      )
    )
      return;
    try {
      await productsApi.archive(p.id);
      toast.success('Product archived.');
      void fetch();
    } catch (err) {
      toast.error(err, 'Failed to archive product.');
    }
  }

  return (
    <AppLayout>
      <PageHeader
        title="Products"
        description="Manage your catalog. Products marked active are visible to buyers."
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <Upload size={16} /> Import products
            </Button>
            <Button onClick={() => navigate('/products/new')}>
              <Plus size={16} /> New Product
            </Button>
          </div>
        }
      />

      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input
              label="Search"
              placeholder="Search by product name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <label
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--text-secondary)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as ProductStatus | 'all')
              }
              style={selectStyle}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </Card>

      <Card padding={0}>
        {loading ? (
          <div
            style={{
              padding: 48,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Spinner />
          </div>
        ) : products.length === 0 ? (
          <EmptyState
            title="No products yet"
            description="Add your first product to start selling on PayIQ."
            action={
              <Button onClick={() => navigate('/products/new')}>
                <Plus size={16} /> Add a product
              </Button>
            }
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 14,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: 'var(--bg-muted)',
                    color: 'var(--text-secondary)',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  <th style={th}>Product</th>
                  <th style={th}>Price</th>
                  <th style={th}>Stock</th>
                  <th style={th}>Status</th>
                  <th style={th}>Created</th>
                  <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr
                    key={p.id}
                    style={{ borderTop: '1px solid var(--border)' }}
                  >
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        {p.imageUrls[0] ? (
                          <img
                            src={p.imageUrls[0]}
                            alt=""
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 'var(--radius-sm)',
                              objectFit: 'cover',
                              border: '1px solid var(--border)',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 'var(--radius-sm)',
                              background: 'var(--bg-muted)',
                            }}
                          />
                        )}
                        <div>
                          <div style={{ fontWeight: 600 }}>{p.name}</div>
                          {p.brand ? (
                            <div
                              style={{
                                fontSize: 12,
                                color: 'var(--text-muted)',
                              }}
                            >
                              {p.brand}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td style={td}>
                      {p.hasVariants && p.priceMin !== p.priceMax
                        ? `${formatNgn(p.priceMin ?? p.price)} - ${formatNgn(
                            p.priceMax ?? p.price,
                          )}`
                        : formatNgn(p.priceMin ?? p.price)}
                    </td>
                    <td style={td}>
                      {p.hasVariants ? (
                        p.totalStockQty == null ? (
                          <span style={{ color: 'var(--text-muted)' }}>
                            Variant stock
                          </span>
                        ) : (
                          p.totalStockQty
                        )
                      ) : p.stockQty === null ? (
                        <span style={{ color: 'var(--text-muted)' }}>
                          Unlimited
                        </span>
                      ) : (
                        p.stockQty
                      )}
                    </td>
                    <td style={td}>
                      <Badge variant={STATUS_VARIANT[p.status]}>
                        {p.status}
                      </Badge>
                    </td>
                    <td style={td}>{formatDate(p.createdAt)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <Button
                          variant="secondary"
                          onClick={() => navigate(`/products/${p.id}/edit`)}
                          style={{ padding: '6px 10px', height: 'auto' }}
                        >
                          <Edit3 size={14} /> Edit
                        </Button>
                        {p.status !== 'archived' ? (
                          <Button
                            variant="ghost"
                            onClick={() => handleArchive(p)}
                            style={{ padding: '6px 10px', height: 'auto' }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {importOpen && (
        <ProductImportModal
          initialJobId={initialImportJobId}
          onClose={() => {
            setImportOpen(false);
            setInitialImportJobId(null);
          }}
          onImported={() => {
            void fetch();
          }}
        />
      )}
    </AppLayout>
  );
};

const th: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontWeight: 600,
};
const td: React.CSSProperties = {
  padding: '14px 16px',
  verticalAlign: 'middle',
};

const selectStyle: React.CSSProperties = {
  height: 42,
  padding: '0 32px 0 12px',
  fontSize: 14,
  color: 'var(--text-primary)',
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%23566472' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  cursor: 'pointer',
  width: '100%',
};
